// IndexedDBManagerModal.tsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Space,
  Input,
  Progress,
  Tooltip,
  message,
  Spin,
  Badge,
  Tag,
  Empty,
} from "antd";
import {
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { get, getMany, del, keys } from "idb-keyval";
import type { ColumnsType } from "antd/es/table";

// Tipos
interface IndexedDBItem {
  key: string;
  size: number;
  type: string;
  url: string | null;
  lastModified: string;
}

interface StorageInfo {
  used: number;
  quota?: number;
  usedFormatted: string;
  quotaFormatted?: string;
  percentUsed: number;
}

interface IndexedDBManagerModalProps {
  visible: boolean;
  onClose: () => void;
}

// Función para formatear tamaño
const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

// Componente principal
const IndexedDBManagerModal: React.FC<IndexedDBManagerModalProps> = ({
  visible,
  onClose,
}) => {
  // Estados
  const [imageKeys, setImageKeys] = useState<IndexedDBItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);
  const [loadingThumbnails, setLoadingThumbnails] = useState<
    Record<string, boolean>
  >({});

  // Cargar datos al abrir el modal
  useEffect(() => {
    if (visible) {
      loadData();
    }
  }, [visible]);

  // Función para cargar los datos
  const loadData = async (): Promise<void> => {
    setLoading(true);
    try {
      // Obtener todas las claves
      const allKeys = await keys();

      // Filtrar por el texto de búsqueda
      const filteredKeys = allKeys.filter(
        (key) =>
          typeof key === "string" &&
          key.toLowerCase().includes(searchText.toLowerCase())
      ) as string[];

      // Obtener información básica para mostrar en la tabla
      const keysInfo = await Promise.all(
        filteredKeys.map(async (key) => {
          try {
            const value = await get(key);

            // Determinar tipo y tamaño
            let type = "Desconocido";
            let size = 0;
            let url = null;

            if (value instanceof Blob) {
              type = value.type || "blob";
              size = value.size;
              if (type.startsWith("image/")) {
                // No crear URL aquí para evitar saturar memoria
                // Se crearán bajo demanda
              }
            } else if (typeof value === "string") {
              type = "text";
              size = new Blob([value]).size;
            } else if (value !== null && typeof value === "object") {
              type = "object";
              size = new Blob([JSON.stringify(value)]).size;
            }

            return {
              key,
              size,
              type,
              url,
              lastModified: new Date().toLocaleString(),
            };
          } catch (error) {
            console.error(
              `Error al obtener información para la clave ${key}:`,
              error
            );
            return {
              key,
              size: 0,
              type: "Error",
              url: null,
              lastModified: "N/A",
            };
          }
        })
      );

      // Ordenar por tamaño (descendente)
      keysInfo.sort((a, b) => b.size - a.size);

      setImageKeys(keysInfo);

      // Calcular uso de almacenamiento
      const totalSize = keysInfo.reduce((acc, item) => acc + item.size, 0);

      // Obtener estimación de cuota
      if (navigator.storage && navigator.storage.estimate) {
        const estimate = await navigator.storage.estimate();
        setStorageInfo({
          used: totalSize,
          quota: estimate.quota,
          usedFormatted: formatBytes(totalSize),
          quotaFormatted: estimate.quota
            ? formatBytes(estimate.quota)
            : undefined,
          percentUsed: estimate.quota
            ? Math.round((totalSize / estimate.quota) * 100)
            : 0,
        });
      } else {
        setStorageInfo({
          used: totalSize,
          usedFormatted: formatBytes(totalSize),
          percentUsed: 0,
        });
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      message.error("Error al cargar datos de IndexedDB");
    } finally {
      setLoading(false);
    }
  };

  // Cargar thumbnail bajo demanda
  const loadThumbnail = async (key: string): Promise<void> => {
    if (loadingThumbnails[key]) return;

    setLoadingThumbnails((prev) => ({ ...prev, [key]: true }));

    try {
      const value = await get(key);

      if (value instanceof Blob && value.type.startsWith("image/")) {
        const url = URL.createObjectURL(value);

        // Actualizar la URL en el estado
        setImageKeys((prev) =>
          prev.map((item) => (item.key === key ? { ...item, url } : item))
        );
      }
    } catch (error) {
      console.error(`Error al cargar thumbnail para ${key}:`, error);
    } finally {
      setLoadingThumbnails((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Eliminar elementos seleccionados
  const handleDelete = async (): Promise<void> => {
    if (selectedRowKeys.length === 0) return;

    Modal.confirm({
      title: "Confirmar eliminación",
      content: `¿Estás seguro de eliminar ${selectedRowKeys.length} elemento(s)? Esta acción no se puede deshacer.`,
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          setLoading(true);

          // Liberar URLs primero
          imageKeys
            .filter((item) => selectedRowKeys.includes(item.key) && item.url)
            .forEach((item) => {
              if (item.url) URL.revokeObjectURL(item.url);
            });

          // Eliminar cada elemento
          await Promise.all(selectedRowKeys.map((key) => del(key.toString())));

          message.success(`${selectedRowKeys.length} elemento(s) eliminado(s)`);
          setSelectedRowKeys([]);

          // Recargar datos
          await loadData();
        } catch (error) {
          console.error("Error al eliminar elementos:", error);
          message.error("Error al eliminar elementos");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Columnas de la tabla
  const columns: ColumnsType<IndexedDBItem> = [
    // {
    //   title: "ID",
    //   dataIndex: "key",
    //   key: "key",
    //   ellipsis: true,
    //   width: 250,
    //   render: (text) => (
    //     <Tooltip title={text} placement="topLeft">
    //       <span>{text}</span>
    //     </Tooltip>
    //   ),
    // },
    {
      title: "Acciones",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRowKeys([record.key]);
              handleDelete();
            }}
          />
        </Space>
      ),
    },
    {
      title: "Vista Previa",
      dataIndex: "url",
      key: "preview",
      width: 120,
      render: (url, record) => {
        if (record.type.startsWith("image/")) {
          if (url) {
            return (
              <img
                src={url}
                alt={record.key}
                style={{ maxWidth: 100, maxHeight: 100, objectFit: "contain" }}
              />
            );
          } else {
            return (
              <Button
                type="default"
                size="small"
                onClick={() => loadThumbnail(record.key)}
                loading={loadingThumbnails[record.key]}
              >
                Ver imagen
              </Button>
            );
          }
        } else {
          return <Tag color="default">{record.type}</Tag>;
        }
      },
    },
    {
      title: "Tamaño",
      dataIndex: "size",
      key: "size",
      width: 100,
      sorter: (a, b) => a.size - b.size,
      render: (size) => formatBytes(size),
    },
    {
      title: "Tipo",
      dataIndex: "type",
      key: "type",
      width: 120,
      filters: [
        { text: "Imagen", value: "image/" },
        { text: "Texto", value: "text" },
        { text: "Objeto", value: "object" },
        { text: "Otros", value: "other" },
      ],
      onFilter: (value, record) => {
        if (value === "other") {
          return (
            !record.type.startsWith("image/") &&
            record.type !== "text" &&
            record.type !== "object"
          );
        }
        return record.type.startsWith(value.toString());
      },
      render: (type) => {
        let color = "default";
        if (type.startsWith("image/")) color = "green";
        else if (type === "text") color = "blue";
        else if (type === "object") color = "orange";
        else if (type === "Error") color = "red";

        return <Tag color={color}>{type}</Tag>;
      },
    },
    {
      title: "Última Modificación",
      dataIndex: "lastModified",
      key: "lastModified",
      width: 180,
    },
  ];

  // Manejar búsqueda
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchText(e.target.value);
  };

  // Efecto para buscar después de esperar
  useEffect(() => {
    const timer = setTimeout(() => {
      loadData();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Limpiar recursos cuando se cierra el modal
  useEffect(() => {
    return () => {
      // Liberar todas las URLs creadas
      imageKeys.forEach((item) => {
        if (item.url) {
          URL.revokeObjectURL(item.url);
        }
      });
    };
  }, []);

  return (
    <Modal
      title="Gestor de Almacenamiento IndexedDB"
      open={visible}
      onCancel={onClose}
      width={1000}
      centered
      zIndex={1200}
      footer={[
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
        <Button
          key="delete"
          type="primary"
          danger
          disabled={selectedRowKeys.length === 0}
          onClick={handleDelete}
          style={{ marginRight: 5 }}
        >
          Eliminar ({selectedRowKeys.length})
        </Button>,
        <Badge count={imageKeys.length}>
          <Button type="default" onClick={loadData} icon={<ReloadOutlined />}>
            Actualizar
          </Button>
        </Badge>,
      ]}
      bodyStyle={{ maxHeight: "70vh", overflow: "auto" }}
    >
      <div style={{ marginBottom: 16 }}>
        {storageInfo && (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
            >
              <span style={{ marginRight: 8 }}>Uso de almacenamiento:</span>
              <Progress
                percent={storageInfo.percentUsed}
                status={storageInfo.percentUsed > 80 ? "exception" : "active"}
                style={{ flex: 1 }}
              />
              <span style={{ marginLeft: 8 }}>
                {storageInfo.usedFormatted}
                {storageInfo.quota && ` de ${storageInfo.quotaFormatted}`}
              </span>
            </div>
            {storageInfo.percentUsed > 70 && (
              <div style={{ marginTop: 8, color: "#ff4d4f" }}>
                <WarningOutlined /> Advertencia: El almacenamiento está llegando
                a su límite.
              </div>
            )}
          </div>
        )}

        {/* <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <Space>
            <Input
              placeholder="Buscar por ID"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearch}
              style={{ width: 250 }}
              allowClear
            />
          </Space>
          <Space>
            <Tooltip title="Esta tabla muestra todas las entradas almacenadas en IndexedDB usando idb-keyval">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        </div> */}
      </div>

      <Spin spinning={loading}>
        {imageKeys.length > 0 ? (
          <Table
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            }}
            columns={columns}
            dataSource={imageKeys}
            rowKey="key"
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total) => `Total: ${total} elementos`,
            }}
            scroll={{ y: 400 }}
          />
        ) : (
          <Empty description="No hay elementos en el almacenamiento" />
        )}
      </Spin>
    </Modal>
  );
};

export default IndexedDBManagerModal;
